import { Box, CircularProgress, IconButton, Skeleton } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';

/** Same path segment as backend file routes — not a public URL; browser must download with auth (axios). */
export function isProtectedAnnouncementFileUrl(url: string) {
  return /\/noti-service\/announcement\/files\//i.test(url ?? '');
}

export function isRemoteMediaVideo(item: { type?: string; url?: string }) {
  const ty = (item.type ?? '').toLowerCase();
  if (ty === 'video') return true;
  return /\.(mp4|webm|ogg)(\?|$)/i.test(item.url ?? '');
}

export function guessMimeFromUrl(url: string, isVideo: boolean): string {
  if (isVideo) return 'video/mp4';
  const u = (url ?? '').toLowerCase();
  if (u.endsWith('.png')) return 'image/png';
  if (u.endsWith('.webp')) return 'image/webp';
  return 'image/jpeg';
}

export type ProtectedPreviewEntry = { objectUrl?: string; loading?: boolean; error?: string };

export type MediaThumbRender =
  | { status: 'loading' }
  | { status: 'video'; src: string }
  | { status: 'video_placeholder' }
  | { status: 'image'; src: string };

export function resolveProtectedMediaThumb(
  url: string,
  m: { type?: string; url?: string },
  protectedEntry?: ProtectedPreviewEntry,
): MediaThumbRender {
  const video = isRemoteMediaVideo(m);
  if (!isProtectedAnnouncementFileUrl(url)) {
    return video ? { status: 'video', src: url } : { status: 'image', src: url };
  }
  if (protectedEntry?.loading && !protectedEntry?.objectUrl) {
    return { status: 'loading' };
  }
  if (protectedEntry?.objectUrl) {
    return video
      ? { status: 'video', src: protectedEntry.objectUrl }
      : { status: 'image', src: protectedEntry.objectUrl };
  }
  if (video) {
    return { status: 'video_placeholder' };
  }
  return { status: 'image', src: url };
}

const VIDEO_THUMB_OVERLAY_SX = {
  position: 'absolute' as const,
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 45%)',
  pointerEvents: 'none' as const,
};

export const THUMB_BOX_SX = {
  position: 'relative' as const,
  height: { xs: 140, sm: 160 },
  bgcolor: 'grey.900' as const,
};

export function AnnouncementMediaThumb({ thumb }: { thumb: MediaThumbRender }) {
  if (thumb.status === 'loading') {
    return (
      <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
        <Skeleton variant="rectangular" sx={{ width: '100%', height: '100%', bgcolor: 'grey.900' }} />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <CircularProgress size={28} />
        </Box>
      </Box>
    );
  }
  if (thumb.status === 'video_placeholder') {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          bgcolor: 'grey.900',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <PlayCircleOutlineIcon
          sx={{
            fontSize: 52,
            color: 'common.white',
            opacity: 0.95,
            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))',
          }}
        />
      </Box>
    );
  }
  if (thumb.status === 'video') {
    return (
      <>
        <Box
          component="video"
          src={thumb.src}
          muted
          playsInline
          preload="metadata"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            verticalAlign: 'bottom',
          }}
        />
        <Box sx={VIDEO_THUMB_OVERLAY_SX}>
          <PlayCircleOutlineIcon
            sx={{
              fontSize: 52,
              color: 'common.white',
              opacity: 0.95,
              filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))',
            }}
          />
        </Box>
      </>
    );
  }
  return (
    <Box
      component="img"
      src={thumb.src}
      alt=""
      sx={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
      }}
    />
  );
}

export function remoteMediaLabel(url: string) {
  try {
    const u = new URL(url);
    const seg = u.pathname.split('/').filter(Boolean);
    return seg.length ? seg[seg.length - 1] : url;
  } catch {
    const parts = url.split('/');
    return parts.length ? parts[parts.length - 1] : url;
  }
}

export function AnnouncementMediaLightboxDialog({
  remoteLightbox,
  onClose,
}: {
  remoteLightbox: { url: string; isVideo: boolean; label: string } | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={Boolean(remoteLightbox)}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'common.black',
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 2,
          color: 'common.white',
          bgcolor: 'rgba(0,0,0,0.5)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
        }}>
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {remoteLightbox &&
          (remoteLightbox.isVideo ? (
            <Box
              component="video"
              src={remoteLightbox.url}
              controls
              playsInline
              autoPlay
              sx={{
                width: '100%',
                maxHeight: '85vh',
                display: 'block',
                outline: 'none',
              }}
            />
          ) : (
            <Box
              component="img"
              src={remoteLightbox.url}
              alt={remoteLightbox.label}
              sx={{
                width: '100%',
                maxHeight: '85vh',
                objectFit: 'contain',
                display: 'block',
                verticalAlign: 'bottom',
              }}
            />
          ))}
        {remoteLightbox && (
          <Typography
            variant="body2"
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              py: 1.5,
              px: 2,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
              color: 'common.white',
            }}>
            {remoteLightbox.label}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
